import { apiClient } from './apiClient'

export type PollStatus = 'ACTIVE' | 'ARCHIVED'
export type PollFrequency = 'AFTER_EACH_ROUND' | 'WEEKLY' | 'MONTHLY' | 'ANNUAL'
export type PollQuestionType = 'BOOLEAN' | 'SCALE'

export interface PollQuestionDTO {
  id: number
  text: string
  type: PollQuestionType
  displayOrder: number
}

export interface PollDTO {
  id: number
  scorecardId: number
  status: PollStatus
  frequency: PollFrequency
  createdAt?: string
  archivedAt?: string | null
  questions: PollQuestionDTO[]
  responseCount: number
}

export interface QuestionInput {
  text: string
  type: PollQuestionType
}

export interface CreatePollRequest {
  frequency: PollFrequency
  questions: QuestionInput[]
}

export interface AnswerInput {
  questionId: number
  booleanValue?: boolean
  scaleValue?: number
}

export interface SubmitPollResponseRequest {
  resultId: number
  answers: AnswerInput[]
  comment?: string
}

export interface PollCommentDTO {
  submittedAt: string
  comment: string
}

export interface TrendBucketDTO {
  month: string
  count: number
  average?: number
  truePercentage?: number
}

export interface QuestionResultDTO {
  questionId: number
  text: string
  type: PollQuestionType
  displayOrder: number
  distribution?: Record<string, number>
  average?: number
  median?: number
  trueCount?: number
  falseCount?: number
  truePercentage?: number
  trend: TrendBucketDTO[]
}

export interface PollResultsDTO {
  pollId: number
  scorecardId: number
  status: PollStatus
  frequency: PollFrequency
  createdAt?: string
  archivedAt?: string | null
  responseCount: number
  questions: QuestionResultDTO[]
  comments: PollCommentDTO[]
}

class PollService {
  // Admin (owner configuration)
  listPolls = async (scorecardId: number): Promise<PollDTO[]> => {
    const response = await apiClient.get<PollDTO[]>(`/admin/scorecards/${scorecardId}/polls`, {
      suppressErrorToast: true
    } as Record<string, unknown>)
    return response.data
  }

  createPoll = async (scorecardId: number, request: CreatePollRequest): Promise<PollDTO> => {
    const response = await apiClient.post<PollDTO>(`/admin/scorecards/${scorecardId}/polls`, request)
    return response.data
  }

  updatePoll = async (scorecardId: number, pollId: number, request: CreatePollRequest): Promise<PollDTO> => {
    const response = await apiClient.put<PollDTO>(`/admin/scorecards/${scorecardId}/polls/${pollId}`, request)
    return response.data
  }

  replacePoll = async (scorecardId: number, pollId: number, request: CreatePollRequest): Promise<PollDTO> => {
    const response = await apiClient.post<PollDTO>(`/admin/scorecards/${scorecardId}/polls/${pollId}/replace`, request)
    return response.data
  }

  archivePoll = async (scorecardId: number, pollId: number): Promise<void> => {
    await apiClient.post(`/admin/scorecards/${scorecardId}/polls/${pollId}/archive`)
  }

  getResults = async (scorecardId: number, pollId: number): Promise<PollResultsDTO> => {
    const response = await apiClient.get<PollResultsDTO>(`/admin/scorecards/${scorecardId}/polls/${pollId}/results`, {
      suppressErrorToast: true
    } as Record<string, unknown>)
    return response.data
  }

  // Player (post-round prompt)
  getPendingPoll = async (resultId: number): Promise<PollDTO | null> => {
    const response = await apiClient.get<PollDTO | null>('/polls/pending', {
      params: { resultId },
      suppressErrorToast: true
    } as Record<string, unknown>)
    return response.data
  }

  submitResponse = async (pollId: number, request: SubmitPollResponseRequest): Promise<void> => {
    await apiClient.post(`/polls/${pollId}/responses`, request)
  }

  dismiss = async (pollId: number, resultId: number): Promise<void> => {
    await apiClient.post(`/polls/${pollId}/dismiss`, { resultId }, { suppressErrorToast: true } as Record<string, unknown>)
  }
}

export const pollService = new PollService()
